/**
 * Crisis detection — deterministic, never model-judged.
 *
 * Shared by the client (mood check-in notes) and the server (coach chat), so
 * both surfaces behave identically. An LLM must never be the thing that decides
 * whether someone is in danger: it can miss, it can be talked out of it, and it
 * can be inconsistent between calls. This is plain pattern matching.
 *
 * DESIGN BIAS: err toward showing support. A false positive shows someone a
 * gentle, dismissible card with real resources — a minor annoyance. A false
 * negative is catastrophic. Where the two trade off, we accept false positives.
 *
 * BUT: fitness apps are a false-positive minefield ("killer workout", "this set
 * is killing me", "deadlift", "I'm dead"). Gym slang is stripped before matching
 * so the card stays rare enough that people take it seriously when it appears.
 */

/** Gym/hyperbole phrases neutralized before matching. Order matters. */
const GYM_SLANG = [
  /\bdead\s?lift(s|ing|ed)?\b/gi,
  /\bkill(ed|ing|er)?\s+(it|that|this|the)\b/gi,   // "killed it", "killing that set"
  /\b(this|that|these|it)\s+(workout|set|session|run|leg day|wod)?\s*(is|was|are)?\s*kill(ing|ed)?\s+me\b/gi,
  /\b(dying|dyin')\s+to\b/gi,                       // "dying to try"
  /\b(i'?m|im)\s+(so\s+)?dead\b/gi,                 // "I'm dead" after a session
  /\bdead\s+(tired|beat|legs|arms)\b/gi,
  /\bkiller\s+(workout|session|set|leg day)\b/gi,
  /\bsore\s+as\s+(hell|death)\b/gi,
];

/**
 * High-precision self-harm / suicidality patterns. Each requires an explicit
 * self-referential target or unambiguous term — not just "die" or "kill".
 */
export const CRISIS_PATTERNS = [
  // t? handles doubled consonants: cut -> cutting
  /\b(kill|hurt|harm|cut)(t?ing|t?ed)?\s+my\s?self\b/i,
  /\bsuicid(e|al)\b/i,
  /\bself[-\s]?harm(ing|ed)?\b/i,
  /\b(want|wanna|wish|wished)\s+(i\s+(was|were)\s+)?(to\s+)?(be\s+)?d(ie|ead)\b/i,
  /\bbetter\s+off\s+dead\b/i,
  /\bdon'?t\s+want\s+to\s+(live|be\s+here|exist|wake\s+up)\b/i,
  /\bno\s+(reason|point)\s+(to|in)\s+(live|living|going\s+on)\b/i,
  /\bend(ing)?\s+(my\s+life|it\s+all)\b/i,
  /\btake\s+my\s+own\s+life\b/i,
  /\bnot\s+worth\s+living\b/i,
  /\beveryone\s+would\s+be\s+better\s+(off\s+)?without\s+me\b/i,
];

/** Strip gym hyperbole so it can't trigger (or mask) a match. */
function stripGymSlang(text) {
  let out = String(text || "");
  for (const re of GYM_SLANG) out = out.replace(re, " ");
  return out;
}

/**
 * True if the text contains explicit self-harm or suicidality signals.
 * Safe to call on any user text; never throws.
 */
export function detectCrisis(text) {
  if (!text || typeof text !== "string") return false;
  const cleaned = stripGymSlang(text);
  return CRISIS_PATTERNS.some((re) => re.test(cleaned));
}

/** Resources shown to the user. US-centric for now. */
export const CRISIS_RESOURCES = {
  headline: "You don't have to handle this alone.",
  body: "If you're going through something heavy, talking to someone helps. These are free, confidential, and available 24/7.",
  lines: [
    { label: "Call or text 988", detail: "Suicide & Crisis Lifeline", href: "tel:988" },
    { label: "Text HOME to 741741", detail: "Crisis Text Line", href: "sms:741741&body=HOME" },
  ],
  // This product is prescribed by licensed clinicians. A wellness-app
  // disclaimer ("we're not therapy") is both false here and useless as a
  // safety statement — the patient IS in therapy, and what they need in this
  // moment is to be pointed at a human tonight, not reminded of a category.
  footer: "This app isn't emergency care and nobody is reading this screen. If you're in danger right now, call 988 or 911. Your clinician should hear about this at your next session — or sooner.",
};

/**
 * ACUTE escalation — a single reading at the bottom of the scale.
 *
 * The sustained rule below needs seven consecutive days AND seven stored
 * check-ins, which means it is mathematically dead for a patient's entire
 * first week — precisely the week a newly-referred patient is most likely to
 * be acutely unwell. It also cannot see the evening reading at all.
 *
 * So there is a second, blunter rule: a patient who reports the bottom of the
 * mood scale gets support offered on that reading, immediately, every time.
 * Deterministic, no history required, no model in the path.
 *
 * This will fire on people who are not in danger. That is the correct error to
 * make: the cost of showing a crisis line to someone having a merely terrible
 * day is that they scroll past it, and the surface is deliberately written so
 * that scrolling past costs nothing.
 */
export const ACUTE_MOOD_AT_OR_BELOW = 1;

export function isAcuteReading(mood) {
  return typeof mood === "number" && mood <= ACUTE_MOOD_AT_OR_BELOW;
}

/**
 * Sustained-low escalation: gentle nudge toward real support after a stretch of
 * consistently low mood. Deliberately conservative — this should feel like a
 * friend noticing, not an alarm.
 *
 * @param {Array<{date:string, mood:number}>} checkins - daily check-ins, any order
 * @param {number} days - consecutive-day window (default 7)
 * @param {number} threshold - mood at or below this counts as low (default 2)
 */
export function shouldSuggestSupport(checkins, days = 7, threshold = 2) {
  const daily = (checkins || [])
    .filter((c) => c.context === "daily" && typeof c.mood === "number")
    .sort((a, b) => (a.date < b.date ? 1 : -1)); // newest first

  // A short history is not evidence of wellbeing. The acute rule above covers
  // the first week; this one simply declines to claim a *pattern* it cannot
  // see yet, rather than declaring the patient fine.
  if (daily.length < days) return false;

  // Need `days` distinct most-recent days, all at/below threshold.
  const seen = new Set();
  let lowRun = 0;
  for (const c of daily) {
    if (seen.has(c.date)) continue;
    seen.add(c.date);
    if (c.mood <= threshold) lowRun++;
    else return false;
    if (lowRun >= days) return true;
  }
  return false;
}
